
/**
 * apiClient.ts
 * * Centralized utility for making HTTP requests using Axios.
 * Provides consistent configuration, typing, and error handling.
 * * NOTE: You must install Axios and the types: 
 * * npm install axios 
 * * npm install -D typescript @types/node @types/axios
 */

import { useAuth } from '@/context/AuthContext';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Load the base URL from the environment variables.
// In Next.js, use NEXT_PUBLIC_ prefix for client-side access.

const URL: string = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000/api';



let currentAuthToken: string | null = null;

/**
 * Public function to set the authentication token from your component state.
 * You will call this function whenever your authentication state changes.
 */
export const setAuthToken = (token: string | null) => {
  currentAuthToken = token;
  console.log('Auth token updated:', token ? 'Token set' : 'Token cleared');
  console.log(currentAuthToken)
};

/**
 * Create a new Axios instance with default configuration.
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: URL,
  headers: {
    'Content-Type': 'application/json',
    // Add Authorization header here if needed (e.g., token)
    'Authorization': `Bearer ${currentAuthToken}`,
  },
});

/**
 * Axios **Interceptors** are the recommended way to handle global logic 
 * like authentication, logging, and error handling.
 */

// --- Request Interceptor ---
// Uses AxiosRequestConfig type
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Example: If you need to dynamically add a token on every request
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers = {
    //     ...config.headers,
    //     'Authorization': `Bearer ${token}`,
    //   };
    // }
    return config;
  },
  (error) => {
    // Handle request error (e.g., network issues)
    return Promise.reject(error);
  }
);


// --- Response Interceptor ---
// Handles global error responses (4xx, 5xx) and transforms the response structure.
// Uses AxiosResponse and AxiosError types
apiClient.interceptors.response.use(
  // Success Handler (Status 2xx)
  (response: AxiosResponse) => {
    // Check for 204 No Content response
    if (response.status === 204) {
      return null as any; // Return null for 204
    }
    return response.data; // <--- The data is returned directly on success
  },
  
  // Error Handler (Status outside 2xx)
  (error: AxiosError) => {
    // Axios wraps the HTTP response in `error.response`
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { data, status, statusText } = error.response;

      // Extract detailed error message from the backend response if available
      // Note: `data` type is `unknown` by default, so we cast it to an object.
      const responseData = data as { message?: string };
      const errorDetail: string = responseData?.message || statusText || 'An unknown API error occurred.';

      // Create a structured error with specific properties
      const structuredError = new Error(
        `Request failed with status ${status}: ${errorDetail}`
      ) as Error & { status: number, data: unknown };
        
      structuredError.status = status;
      structuredError.data = data;
      
      // Re-throw the structured error for the calling service/component
      return Promise.reject(structuredError); 
    } else if (error.request) {
      // The request was made but no response was received (e.g., network down)
      const networkError = new Error('Network Error: No response received from server.');
      return Promise.reject(networkError);
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(error);
    }
  }
);


// Export the configured instance for use in service modules (e.g., vendorsApi.ts)
export default apiClient;
